// componentTokens.ts
import type { TokenMap } from './types';

// The user's font scale lives on the root font size (html { font-size }), so
// the rem-based --nxt-font-size-* steps already follow it; a component size
// token just references the step — never multiply by --nxt-font-scale.
const size = (step: string) => `var(--nxt-font-size-${step})`;

export const COMPONENT_TOKENS: Record<string, string> = {
  // ── Buttons ──
  'button-bg': 'var(--nxt-brand-bg)',
  'button-bg-hover': 'var(--nxt-brand-bg-hover)',
  'button-bg-active': 'var(--nxt-brand-bg-active)',
  'button-bg-focus': 'var(--nxt-brand-bg-focus)',
  'button-bg-disabled': 'var(--nxt-disabled-bg)',
  'button-fg': 'var(--nxt-brand-fg)',
  'button-fg-hover': 'var(--nxt-brand-fg-hover)',
  'button-fg-disabled': 'var(--nxt-disabled-fg)',
  'button-border': 'var(--nxt-brand-border)',
  'button-border-hover': 'var(--nxt-brand-border-hover)',
  'button-border-active': 'var(--nxt-brand-border-active)',
  'button-ring': 'var(--nxt-focus-ring)',
  'button-size': size('sm'),
  'icon-button-bg': 'transparent',
  'icon-button-bg-hover': 'var(--nxt-surface-sunken-bg)',
  'icon-button-bg-active': 'var(--nxt-neutral-subtle-bg-hover)',
  'icon-button-bg-disabled': 'var(--nxt-disabled-bg)',
  'icon-button-fg': 'var(--nxt-fg-subtle)',
  'icon-button-fg-hover': 'var(--nxt-fg)',
  'icon-button-fg-disabled': 'var(--nxt-disabled-fg)',
  'icon-button-ring': 'var(--nxt-focus-ring)',
  'icon-button-size': size('md'),
  'utility-button-bg': 'transparent',
  'utility-button-bg-hover': 'var(--nxt-surface-sunken-bg)',
  'utility-button-bg-active': 'var(--nxt-neutral-subtle-bg-hover)',
  'utility-button-fg': 'var(--nxt-fg-subtle)',
  'utility-button-fg-hover': 'var(--nxt-fg)',
  'utility-button-size': size('sm'),
  'fab-bg': 'var(--nxt-brand-bg)',
  'fab-bg-hover': 'var(--nxt-brand-bg-hover)',
  'fab-bg-active': 'var(--nxt-brand-bg-active)',
  'fab-fg': 'var(--nxt-brand-fg)',
  'fab-ring': 'var(--nxt-focus-ring)',

  // ── Inputs ──
  'input-bg-focus': 'var(--nxt-surface-bg)',
  'input-bg-disabled': 'var(--nxt-disabled-bg)',
  'input-fg': 'var(--nxt-fg)',
  'input-placeholder': 'var(--nxt-fg-muted)',
  'input-fg-disabled': 'var(--nxt-disabled-fg)',
  'input-border': 'var(--nxt-border)',
  'input-border-hover': 'var(--nxt-border-strong)',
  'input-border-focus': 'var(--nxt-brand-bg)',
  'input-border-invalid': 'var(--nxt-danger-bg)',
  'input-ring': 'var(--nxt-focus-ring-soft)',
  'input-ring-invalid': 'var(--nxt-danger-ring-soft)',
  'input-size': size('sm'),
  'select-bg': 'var(--nxt-input-bg)',
  'select-bg-hover': 'var(--nxt-input-bg-hover)',
  'select-bg-focus': 'var(--nxt-surface-bg)',
  'select-bg-disabled': 'var(--nxt-disabled-bg)',
  'select-fg': 'var(--nxt-fg)',
  'select-border': 'var(--nxt-border)',
  'select-border-focus': 'var(--nxt-brand-bg)',
  'select-icon': 'var(--nxt-fg-muted)',
  'checkbox-bg': 'var(--nxt-surface-bg)',
  'checkbox-bg-checked': 'var(--nxt-brand-bg)',
  'checkbox-bg-disabled': 'var(--nxt-disabled-bg)',
  'checkbox-border': 'var(--nxt-border-strong)',
  'checkbox-border-hover': 'var(--nxt-brand-bg)',
  'checkbox-fg': 'var(--nxt-brand-fg)',
  'checkbox-ring': 'var(--nxt-focus-ring-soft)',
  'radio-bg': 'var(--nxt-surface-bg)',
  'radio-bg-checked': 'var(--nxt-brand-bg)',
  'radio-bg-disabled': 'var(--nxt-disabled-bg)',
  'radio-border': 'var(--nxt-border-strong)',
  'radio-border-hover': 'var(--nxt-brand-bg)',
  'radio-dot': 'var(--nxt-brand-bg)',
  'radio-ring': 'var(--nxt-focus-ring-soft)',
  'switch-track-bg': 'var(--nxt-border-strong)',
  'switch-track-bg-on': 'var(--nxt-brand-bg)',
  'switch-track-bg-disabled': 'var(--nxt-disabled-bg)',
  'switch-thumb-bg': '#ffffff',
  'switch-ring': 'var(--nxt-focus-ring-soft)',
  'slider-track-bg': 'var(--nxt-border)',
  'slider-track-fill': 'var(--nxt-brand-bg)',
  'slider-thumb-bg': '#ffffff',
  'slider-thumb-border': 'var(--nxt-brand-bg)',
  'slider-ring': 'var(--nxt-focus-ring-soft)',

  // ── Selection / lists ──
  'chip-bg': 'var(--nxt-surface-sunken-bg)',
  'chip-bg-hover': 'var(--nxt-input-bg-hover)',
  'chip-bg-selected': 'var(--nxt-brand-subtle-bg)',
  'chip-bg-disabled': 'var(--nxt-disabled-bg)',
  'chip-fg': 'var(--nxt-fg-subtle)',
  'chip-fg-selected': 'var(--nxt-brand-subtle-fg)',
  'chip-border': 'var(--nxt-border)',
  'chip-border-selected': 'var(--nxt-brand-border)',
  'chip-size': size('xs'),
  'toggle-bg': 'transparent',
  'toggle-bg-hover': 'var(--nxt-surface-sunken-bg)',
  'toggle-bg-selected': 'var(--nxt-brand-subtle-bg)',
  'toggle-fg': 'var(--nxt-fg-subtle)',
  'toggle-fg-selected': 'var(--nxt-brand-subtle-fg)',
  'toggle-border': 'var(--nxt-border)',
  'toggle-size': size('sm'),
  'menu-item-bg': 'transparent',
  'menu-item-bg-hover': 'var(--nxt-brand-subtle-bg)',
  'menu-item-bg-active': 'var(--nxt-brand-subtle-bg)',
  'menu-item-bg-disabled': 'transparent',
  'menu-item-fg': 'var(--nxt-fg)',
  'menu-item-fg-disabled': 'var(--nxt-disabled-fg)',
  'menu-item-size': size('sm'),

  // ── Navigation ──
  'nav-item-bg': 'transparent',
  'nav-item-bg-hover': 'var(--nxt-nav-hover-bg)',
  'nav-item-bg-current': 'var(--nxt-nav-active-bg)',
  'nav-item-fg': 'var(--nxt-nav-fg)',
  'nav-item-fg-muted': 'var(--nxt-nav-fg-muted)',
  'nav-item-fg-current': 'var(--nxt-nav-fg)',
  'nav-item-accent': 'var(--nxt-nav-accent)',
  'nav-item-size': size('sm'),
  'navbar-bg': 'var(--nxt-nav-bg)',
  'navbar-fg': 'var(--nxt-nav-fg)',
  'navbar-item-hover-bg': 'var(--nxt-nav-hover-bg)',
  'navbar-icon-fg': 'var(--nxt-nav-fg)',
  'navbar-size': size('sm'),
  'tab-fg': 'var(--nxt-fg-subtle)',
  'tab-fg-hover': 'var(--nxt-fg)',
  'tab-fg-selected': 'var(--nxt-brand-bg)',
  'tab-fg-disabled': 'var(--nxt-disabled-fg)',
  'tab-bg-hover': 'var(--nxt-surface-sunken-bg)',
  'tab-indicator': 'var(--nxt-brand-bg)',
  'tab-size': size('sm'),
  'breadcrumb-fg': 'var(--nxt-fg-subtle)',
  'breadcrumb-fg-hover': 'var(--nxt-fg)',
  'breadcrumb-fg-current': 'var(--nxt-fg)',
  'breadcrumb-sep': 'var(--nxt-fg-muted)',
  'breadcrumb-size': size('sm'),
  'rail-bg': 'var(--nxt-surface-bg)',
  'rail-bg-hover': 'var(--nxt-brand-subtle-bg)',
  'rail-bg-active': 'var(--nxt-brand-bg)',
  'rail-fg': 'var(--nxt-fg-subtle)',
  'rail-fg-active': 'var(--nxt-brand-fg)',
  'rail-border': 'var(--nxt-border-strong)',
  'step-bg': 'var(--nxt-surface-sunken-bg)',
  'step-bg-current': 'var(--nxt-brand-bg)',
  'step-bg-done': 'var(--nxt-brand-subtle-bg)',
  'step-fg': 'var(--nxt-fg-subtle)',
  'step-fg-current': 'var(--nxt-brand-fg)',
  'step-connector': 'var(--nxt-border)',

  // ── Surfaces / containers ──
  'section-bg': 'var(--nxt-surface-bg)',
  'section-head-bg': 'var(--nxt-surface-bg)',
  'section-border': 'var(--nxt-border)',
  'section-label-fg': 'var(--nxt-brand-bg)',
  'section-accent': 'var(--nxt-brand-bg)',
  'section-index-bg': 'var(--nxt-surface-bg)',
  'section-index-fg': 'var(--nxt-fg-subtle)',
  'section-index-active-bg': 'var(--nxt-brand-bg)',
  'section-index-active-fg': 'var(--nxt-brand-fg)',
  'panel-bg': 'var(--nxt-surface-bg)',
  'panel-header-bg': 'var(--nxt-surface-bg)',
  'panel-border': 'var(--nxt-border)',
  'panel-shadow': 'var(--nxt-shadow-sm)',
  'modal-bg': 'var(--nxt-surface-raised-bg)',
  'modal-fg': 'var(--nxt-fg)',
  'modal-border': 'var(--nxt-border)',
  'modal-overlay-bg': 'var(--nxt-overlay-bg)',
  'modal-shadow': 'var(--nxt-shadow-lg)',
  'popover-bg': 'var(--nxt-surface-raised-bg)',
  'popover-fg': 'var(--nxt-fg)',
  'popover-border': 'var(--nxt-border)',
  'popover-shadow': 'var(--nxt-shadow-md)',
  'tooltip-bg': 'var(--nxt-nav-bg)',
  'tooltip-fg': 'var(--nxt-nav-fg)',
  'drawer-bg': 'var(--nxt-surface-raised-bg)',
  'drawer-border': 'var(--nxt-border)',
  'drawer-overlay-bg': 'var(--nxt-overlay-bg)',

  // ── Table ──
  'table-cell-fg': 'var(--nxt-fg)',
  'table-cell-muted': 'var(--nxt-fg-muted)',
  'table-sort-icon': 'var(--nxt-fg-muted)',
  'table-add-row-fg': 'var(--nxt-brand-bg)',
  'table-size': size('sm'),

  // ── Status / feedback ──
  'badge-bg': 'var(--nxt-brand-subtle-bg)',
  'badge-fg': 'var(--nxt-brand-subtle-fg)',
  'badge-border': 'var(--nxt-brand-border)',
  'badge-dot': 'currentColor',
  'badge-size': size('xs'),
  'banner-bg': 'var(--nxt-info-subtle-bg)',
  'banner-fg': 'var(--nxt-info-subtle-fg)',
  'banner-border': 'var(--nxt-info-border)',
  'banner-icon': 'var(--nxt-info-bg)',
  'banner-size': size('sm'),
  'status-dot-bg': 'var(--nxt-brand-bg)',
  'status-icon-bg': 'var(--nxt-warning-subtle-bg)',
  'status-icon-fg': 'var(--nxt-warning-subtle-fg)',
  'progress-track-bg': 'var(--nxt-surface-sunken-bg)',
  'progress-fill': 'var(--nxt-brand-bg)',
  'progress-fg': 'var(--nxt-fg-subtle)',
  'toast-bg': 'var(--nxt-surface-raised-bg)',
  'toast-fg': 'var(--nxt-fg)',
  'toast-border': 'var(--nxt-border)',
  'toast-shadow': 'var(--nxt-shadow-lg)',

  // ── Typography ──
  'text-body-fg': 'var(--nxt-fg)',
  'text-body-size': size('md'),
  'heading-fg': 'var(--nxt-fg)',
  'heading-size-1': size('2xl'),
  'heading-size-2': size('xl'),
  'heading-size-3': size('lg'),
  'heading-size-4': size('md'),
  'heading-size-5': size('sm'),
  'heading-size-6': size('xs'),
  'text-subtle-fg': 'var(--nxt-fg-subtle)',
  'text-muted-fg': 'var(--nxt-fg-muted)',
  'label-fg': 'var(--nxt-fg-subtle)',
  'label-size': size('xs'),
  'link-fg': 'var(--nxt-link)',
  'link-fg-hover': 'var(--nxt-link-hover)',
  'link-fg-active': 'var(--nxt-link-active)',
  'code-fg': 'var(--nxt-fg)',
  'code-bg': 'var(--nxt-surface-sunken-bg)',
  'code-size': size('sm'),

  // ── Cross-cutting ──
  'divider-color': 'var(--nxt-border)',
  // Avatar tokens drive only the AvatarStack "+N" overflow bubble (individual
  // avatars use a per-user computed gradient). It should read like an NxChip —
  // a neutral pill that separates from the page and the colourful avatars —
  // rather than the too-light brand tint, so these mirror the chip roles.
  'avatar-bg': 'var(--nxt-surface-sunken-bg)',
  'avatar-fg': 'var(--nxt-fg-subtle)',
  'avatar-border': 'var(--nxt-border)',
  'empty-state-fg': 'var(--nxt-fg-muted)',
  'empty-state-icon': 'var(--nxt-fg-muted)',
  'kbd-bg': 'var(--nxt-surface-sunken-bg)',
  'kbd-fg': 'var(--nxt-fg-subtle)',
};

export function buildComponentTokens(): TokenMap {
  return { ...COMPONENT_TOKENS };
}

export function roleRefsOf(value: string): string[] {
  return [...value.matchAll(/var\((--nxt-[a-z0-9-]+)\)/g)].map((m) => m[1]);
}
